import React, { useState } from 'react'
import axios from 'axios'
import EmptyTablePlaceholder from '../../shared/EmptyTablePlaceholder'
import StatusBadge from '../../shared/StatusBadge'
import ScenarioFormModal from './ScenarioFormModal'
import { BusinessErrorOption, ScenarioRow } from './types'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { admin_alert_automation_scenario_path } from '../../../../routes'

interface AlertScenariosTableProps {
  scenarios: ScenarioRow[]
  business_errors: BusinessErrorOption[]
  period_units: string[]
  sms_max_length: number
  can_create: boolean
  can_update: boolean
  can_destroy: boolean
  can_test: boolean
  localization_context?: LocalizationContextType
}

const AlertScenariosTable = ({
  scenarios,
  business_errors: businessErrors,
  period_units: periodUnits,
  sms_max_length: smsMaxLength,
  can_create: canCreate,
  can_update: canUpdate,
  can_destroy: canDestroy,
  can_test: canTest,
  localization_context: localizationContext
}: AlertScenariosTableProps) => {
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ScenarioRow | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = () => window.location.reload()

  // Deactivating is the reversible way to stop a scenario, so deleting one is rarely what someone
  // meant by a single click on a row.
  const destroy = (scenario: ScenarioRow) => {
    const confirmed = window.confirm(
      `Delete "${scenario.name}"? Deactivate it instead to keep the configuration.`
    )
    if (!confirmed) return

    setError(null)
    axios
      .delete(admin_alert_automation_scenario_path(scenario.id))
      .then(reload)
      .catch((err) => setError(err.response?.data?.error || 'Could not delete the scenario.'))
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="section-heading">
          <div className="section-heading-row">Rules</div>
          <div className="section-heading-row bold">Scenarios</div>
        </div>

        {canCreate && (
          <button type="button" className="btn btn-dark" onClick={() => setCreating(true)}>
            + Create Scenario
          </button>
        )}
      </div>

      {error && <div className="text-danger small mb-2">{error}</div>}

      {scenarios.length === 0 ? (
        <EmptyTablePlaceholder
          heading="No scenarios yet"
          subtext="Create one to choose who gets notified when business errors start repeating."
        />
      ) : (
        <div className="table-responsive">
          <table className="table table-robis">
            <thead className="thead-robis">
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Business Errors</th>
                <th scope="col">Threshold</th>
                <th scope="col">Recipients</th>
                <th scope="col">Status</th>
                <th scope="col">Last Executed</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {scenarios.map((scenario) => (
                <tr key={scenario.id}>
                  <td className="fw-bold">{scenario.name}</td>
                  <td>{scenario.business_error_codes.join(', ')}</td>
                  <td>{scenario.threshold_label}</td>
                  <td>{scenario.recipients_label}</td>
                  <td>
                    <StatusBadge
                      label={scenario.active ? 'Active' : 'Inactive'}
                      variant={scenario.active ? 'success-filled' : 'neutral-filled'}
                    />
                  </td>
                  <td className="small">
                    {scenario.last_executed_at
                      ? formatTenantDateTime(scenario.last_executed_at, localizationContext)
                      : '—'}
                  </td>
                  <td className="text-end">
                    {canUpdate && (
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => setEditing(scenario)}
                      >
                        Edit
                      </button>
                    )}
                    {canDestroy && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-3 text-danger"
                        onClick={() => destroy(scenario)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <ScenarioFormModal
          businessErrors={businessErrors}
          periodUnits={periodUnits}
          smsMaxLength={smsMaxLength}
          scenario={editing || undefined}
          canTest={canTest}
          onClose={() => { setCreating(false); setEditing(null) }}
          onSaved={reload}
        />
      )}
    </>
  )
}

export default AlertScenariosTable
